import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../services/ApiService';
import LoadingSkeleton from '../components/LoadingSkeleton';

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [viewPartner, setViewPartner] = useState(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [comment, setComment] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const itemsPerPage = 10;

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const result = await apiService.get('/partners/all');
      setPartners(result.partners || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch partners');
    } finally {
      setLoading(false);
    }
  };

  const filteredPartners = useMemo(() => {
    return partners.filter(p => {
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      const searchableText = [
        p._id,
        p.name,
        p.phone_number,
        p.city,
        p.state,
        p.business_name
      ].join(' ').toLowerCase();

      const matchesSearch = searchableText.includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [partners, searchQuery, statusFilter]);

  const paginatedPartners = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredPartners.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredPartners, currentPage]);

  const totalPages = Math.ceil(filteredPartners.length / itemsPerPage);

  const handleUpdateClick = (p) => {
    setSelectedPartner(p);
    setNewStatus(p.status);
    setComment('');
    setIsUpdateModalOpen(true);
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    if (!newStatus || !comment.trim()) {
      alert('Please provide both status and comment');
      return;
    }
    setIsProcessing(true);
    try {
      await apiService.put(`/partners/status/${selectedPartner._id}`, {
        status: newStatus,
        comment: comment.trim()
      });
      await fetchPartners();
      setIsUpdateModalOpen(false);
    } catch (err) {
      alert(err.message || 'Failed to update partner status');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'status-completed';
      case 'pending': return 'status-pending';
      case 'rejected': return 'status-rejected';
      default: return 'status-in_progress';
    }
  };

  const downloadExcel = () => {
    const headers = ['Name', 'Phone', 'Business', 'City', 'Status', 'Joined'];
    const csvContent = [
      headers.join(','),
      ...partners.map(p => [
        p.name || 'N/A',
        p.phone_number || 'N/A',
        p.business_name || 'N/A',
        p.city || 'N/A',
        p.status || 'N/A',
        new Date(p.createdAt).toLocaleDateString('en-GB')
      ].map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `partners_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading && partners.length === 0) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '100px' }}>
      <i className="fas fa-circle-notch fa-spin" style={{ fontSize: '30px', color: 'var(--primary)' }}></i>
    </div>
  );

  return (
    <div className="partners-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '32px', gap: '20px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: 'Outfit', fontSize: '24px', fontWeight: '800', margin: '0', color: 'var(--text-main)' }}>Partner Ecosystem</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>Grow and manage your network of RTO fulfillment partners.</p>
        </div>
        <button className="btn btn-primary" onClick={downloadExcel}>
          <i className="fas fa-file-download"></i>
          <span>Export Network</span>
        </button>
      </div>

      <div className="admin-card" style={{ padding: '0', overflow: 'hidden' }}>
        <div style={{ padding: '24px', borderBottom: '1px solid var(--border-light)', background: 'var(--bg-main)' }} className="grid-responsive">
          <div className="col-9 col-lg-8 col-md-12" style={{ position: 'relative' }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }}></i>
            <input
              type="text"
              placeholder="Search by partner name, business, or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 48px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-light)',
                fontSize: '14px',
                background: 'white',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div className="col-3 col-lg-4 col-md-12">
            <select
              className="btn btn-outline"
              style={{ width: '100%', background: 'white', height: '100%' }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Any Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-main)', textAlign: 'left' }}>
                <th style={tableHeaderStyle}>PARTNER ENTITY</th>
                <th style={tableHeaderStyle}>CONTACT & REACH</th>
                <th style={tableHeaderStyle}>BUSINESS TYPE</th>
                <th style={tableHeaderStyle}>STATUS</th>
                <th style={tableHeaderStyle}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPartners.length > 0 ? paginatedPartners.map(p => (
                <tr key={p._id} style={{
                  borderBottom: '1px solid var(--border-light)',
                  transition: 'background 0.2s'
                }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-light)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px', height: '40px', borderRadius: '12px',
                        background: 'var(--grad-primary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontWeight: '800', fontSize: '14px'
                      }}>
                        {p.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: '700', color: 'var(--text-main)' }}>{p.name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-dim)', fontWeight: '600' }}>
                          ID: {p._id.slice(-8).toUpperCase()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <i className="fas fa-phone-alt" style={{ fontSize: '11px', color: 'var(--primary)' }}></i>
                        <span style={{ fontWeight: '600' }}>{p.phone_number}</span>
                      </div>
                      <div style={{ color: 'var(--text-muted)' }}>{p.city}, {p.state}</div>
                    </div>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ fontWeight: '600', color: 'var(--text-main)', fontSize: '13px' }}>
                      {p.business_name || (
                        <span style={{ color: 'var(--text-dim)', fontStyle: 'italic', fontWeight: '400' }}>Individual Associate</span>
                      )}
                    </div>
                  </td>
                  <td style={tableCellStyle}>
                    <span className={`status-badge ${getStatusClass(p.status)}`} style={{ fontSize: '11px' }}>
                      {p.status || 'Pending'}
                    </span>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-outline" style={{ padding: '8px 12px', fontSize: '12px' }} onClick={() => setViewPartner(p)}>
                        <i className="fas fa-info-circle"></i> View
                      </button>
                      <button className="btn btn-primary" style={{ padding: '8px 12px', fontSize: '12px' }} onClick={() => handleUpdateClick(p)}>
                        <i className="fas fa-user-check"></i> Review
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" style={{ padding: '64px', textAlign: 'center' }}>
                    <div style={{ color: 'var(--text-dim)', fontSize: '48px', marginBottom: '16px', opacity: 0.2 }}>
                      <i className="fas fa-handshake-slash"></i>
                    </div>
                    <p style={{ color: 'var(--text-muted)', fontWeight: '600' }}>No partners matching your criteria.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '24px', borderTop: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-main)' }}>
          <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: '600' }}>
            Displaying {paginatedPartners.length} of {filteredPartners.length} members
          </span>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="btn btn-outline" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
              <i className="fas fa-chevron-left" style={{ fontSize: '10px' }}></i> Previous
            </button>
            <button className="btn btn-outline" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => p + 1)}>
              Next <i className="fas fa-chevron-right" style={{ fontSize: '10px' }}></i>
            </button>
          </div>
        </div>
      </div>

      {/* View Detail Modal */}
      {viewPartner && (
        <div className="modal-overlay" onClick={() => setViewPartner(null)}>
          <div className="admin-card" style={{ maxWidth: '600px', width: '90%', padding: '0', overflow: 'hidden', border: 'none' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '32px', background: 'var(--grad-primary)', color: 'white', position: 'relative' }}>
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '2px', opacity: 0.8, textTransform: 'uppercase', marginBottom: '8px' }}>Partner Dossier</div>
                  <h3 style={{ margin: 0, fontSize: '24px', fontWeight: '800' }}>{viewPartner.name}</h3>
                  <p style={{ margin: '8px 0 0', opacity: 0.8, fontSize: '14px' }}>{viewPartner.business_name || 'Individual Contributor'}</p>
                </div>
                <button style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', width: '36px', height: '36px', borderRadius: '50%', fontSize: '20px' }} onClick={() => setViewPartner(null)}>×</button>
              </div>
            </div>

            <div style={{ padding: '32px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
                <div>
                  <label style={modalLabelStyle}>REGISTRATION EMAIL</label>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-main)' }}>{viewPartner.email || 'Private Account'}</div>
                </div>
                <div>
                  <label style={modalLabelStyle}>DIRECT CONTACT</label>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-main)' }}>{viewPartner.phone_number}</div>
                </div>
                <div>
                  <label style={modalLabelStyle}>BASE OPERATION</label>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-main)' }}>{viewPartner.city}, {viewPartner.state}</div>
                </div>
                <div>
                  <label style={modalLabelStyle}>MEMBER SINCE</label>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-main)' }}>{new Date(viewPartner.createdAt).toLocaleDateString('en-GB')}</div>
                </div>
              </div>

              <div style={{ background: 'var(--bg-main)', padding: '24px', borderRadius: '16px', border: '1px solid var(--border-light)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-dim)', marginBottom: '4px', textTransform: 'uppercase' }}>Current Application Status</div>
                    <span className={`status-badge ${getStatusClass(viewPartner.status)}`} style={{ fontSize: '12px' }}>{viewPartner.status}</span>
                  </div>
                  <button className="btn btn-primary" style={{ padding: '10px 20px' }} onClick={() => { setViewPartner(null); handleUpdateClick(viewPartner); }}>
                    Update Status
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {isUpdateModalOpen && (
        <div className="modal-overlay">
          <div className="admin-card" style={{ maxWidth: '450px', width: '90%', padding: '0', overflow: 'hidden', border: 'none' }}>
            <div style={{ padding: '24px', background: 'var(--grad-primary)', color: 'white', textAlign: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '800' }}>Administrative Review</h3>
              <p style={{ margin: '4px 0 0', opacity: 0.8, fontSize: '12px' }}>Partner: {selectedPartner.name}</p>
            </div>

            <form onSubmit={handleStatusUpdate} style={{ padding: '24px' }}>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>FINAL DECISION</label>
                <select style={inputStyle} value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                  <option value="pending">Keep as Pending</option>
                  <option value="approved">✅ Approve Onboarding</option>
                  <option value="rejected">❌ Reject Application</option>
                </select>
              </div>
              <div className="form-group">
                <label style={labelStyle}>REVIEW NOTES</label>
                <textarea
                  rows="4"
                  style={{ ...inputStyle, resize: 'none' }}
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder="Summarize the reason for this decision (e.g., verification outcome, document status)..."
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setIsUpdateModalOpen(false)}>Discard</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 2 }} disabled={isProcessing}>
                  {isProcessing ? <i className="fas fa-spinner fa-spin"></i> : 'Commit Decision'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.7); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 20px; }
      `}</style>
    </div>
  );
};

const modalLabelStyle = {
  display: 'block',
  fontSize: '10px',
  fontWeight: '800',
  color: 'var(--text-dim)',
  letterSpacing: '1px',
  marginBottom: '4px'
};

const tableHeaderStyle = {
  padding: '16px 24px',
  color: 'var(--text-muted)',
  fontSize: '11px',
  fontWeight: '800',
  letterSpacing: '1px',
  borderBottom: '1px solid var(--border-light)'
};

const tableCellStyle = {
  padding: '20px 24px',
  fontSize: '14px',
  color: 'var(--text-main)'
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  fontWeight: '800',
  fontSize: '11px',
  color: 'var(--text-dim)',
  letterSpacing: '1px'
};

const inputStyle = {
  width: '100%',
  padding: '14px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-light)',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fcfcfc',
  transition: 'all 0.2s'
};

export default Partners;
